import { useCallback, useMemo, useRef, useState, type ReactNode } from "react";

import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
  type DragCancelEvent,
  type DragStartEvent,
  type Modifier,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  rectSwappingStrategy,
  arraySwap,
} from "@dnd-kit/sortable";
import { restrictToParentElement } from "@dnd-kit/modifiers";

import { TOTAL_COLUMNS } from "../../../components/dashboard/DashboardCustomization/constants";
import { useDashboardCustomizationContext } from "../../../components/dashboard/DashboardCustomization/DashboardCustomizationContext";

import styles from "./SortableMultiColumnList.module.scss";
import {
  flattenRowsForReorder,
  formatRowsForRender,
  getFormattedSectionKey,
  getSectionKey,
  isSectionHalfRow,
  withoutBlankSections,
} from "./utils";

import type {
  WebSection,
  WebSectionId,
} from "byzantine/src/dbbl/businessLogic/modules/userDisplayPreferences/types";

type SortableListProps = {
  items: WebSection[];
  onReorder: (next: WebSection[]) => void;
  renderItem: (
    item: WebSection,
    isHalf: boolean,
    isPreview?: boolean
  ) => ReactNode;
  renderHalfRow: (
    id: string,
    isSingleChildHalfRow: boolean,
    children: ReactNode
  ) => ReactNode;
  announcements?: Announcements;
};

/**
 * Generic vertical sortable list. Each `<SortableMultiColumnList>` owns its own `DndContext`
 * and renders `items` dynamically spanning them based on their `SECTION_COLUMN_SIZES` definition.
 */
const SortableMultiColumnList = ({
  items,
  onReorder,
  renderItem,
  renderHalfRow,
  announcements,
}: SortableListProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const [activeId, setActiveId] = useState<string | null>(null);
  const topSortableBoundsRef = useRef<HTMLDivElement>(null);
  const { getSectionSize } = useDashboardCustomizationContext();

  const modifiers = useMemo<Modifier[]>(
    () => [
      (args) => {
        const topContainerRect =
          topSortableBoundsRef.current?.getBoundingClientRect();

        return restrictToParentElement({
          ...args,
          containerNodeRect: topContainerRect ?? args.containerNodeRect,
        });
      },
    ],
    []
  );

  const formattedRows = formatRowsForRender(items, getSectionSize);

  const topLevelItems = useMemo(
    () => formattedRows.map((section) => getFormattedSectionKey(section)),
    [formattedRows]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveId(null);

      if (!over || active.id === over.id) {
        return;
      }

      const activeSectionId = active.id as WebSectionId;
      const overId = over.id as WebSectionId;

      // If we're swapping a half item with another half item
      if (
        getSectionSize(activeSectionId) < TOTAL_COLUMNS &&
        getSectionSize(overId) < TOTAL_COLUMNS
      ) {
        const flattenedFormattedRows = formattedRows.flat();

        const fromRowIndex = flattenedFormattedRows.findIndex(
          (row) => getFormattedSectionKey(row) === activeSectionId
        );
        const toRowIndex = flattenedFormattedRows.findIndex(
          (row) => getFormattedSectionKey(row) === overId
        );
        const output = arraySwap(
          flattenedFormattedRows,
          fromRowIndex,
          toRowIndex
        );
        onReorder(withoutBlankSections(output));
        return;
      }

      // If we're moving a row
      const fromRowIndex = formattedRows.findIndex(
        (row) => getFormattedSectionKey(row) === activeSectionId
      );
      const toRowIndex = formattedRows.findIndex(
        (row) => getFormattedSectionKey(row) === overId
      );
      onReorder(
        flattenRowsForReorder(
          arrayMove(formattedRows, fromRowIndex, toRowIndex)
        )
      );
    },
    [onReorder, formattedRows, getSectionSize]
  );

  const accessibility = useMemo(
    () => (announcements ? { announcements } : undefined),
    [announcements]
  );

  const activeItem = useMemo(() => {
    if (!activeId) return null;
    const foundRow = formattedRows.find(
      (row) => getFormattedSectionKey(row) === activeId
    );
    const foundItem = items.find(
      (row) => getFormattedSectionKey(row) === activeId
    );

    return foundRow ?? foundItem ?? null;
  }, [activeId, formattedRows, items]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  }, []);

  const handleDragCancel = useCallback((_event: DragCancelEvent) => {
    setActiveId(null);
  }, []);

  const dragPreview = useMemo(() => {
    if (activeItem instanceof Array) {
      return renderHalfRow(
        getFormattedSectionKey(activeItem),
        isSectionHalfRow(activeItem),
        activeItem.map((item) => renderItem(item, true, true))
      );
    }
    if (activeItem) {
      return renderItem(activeItem, false, true);
    }
    return null;
  }, [activeItem, renderItem, renderHalfRow]);

  return (
    <DndContext
      autoScroll={false}
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={modifiers}
      onDragEnd={handleDragEnd}
      // These are necessary for dragging between SortableContexts
      onDragStart={handleDragStart}
      onDragCancel={handleDragCancel}
      accessibility={accessibility}
    >
      <DragOverlay>{dragPreview}</DragOverlay>
      <div
        ref={topSortableBoundsRef}
        className={styles["sortable-multi-column-list"]}
      >
        <SortableContext
          items={topLevelItems}
          strategy={verticalListSortingStrategy}
        >
          {formattedRows.map((section) => {
            if (section instanceof Array) {
              return renderHalfRow(
                getSectionKey(section),
                isSectionHalfRow(section),
                <SortableContext
                  items={section.map(({ section_id }) => section_id)}
                  strategy={rectSwappingStrategy}
                >
                  {section.map((item) => renderItem(item, true))}
                </SortableContext>
              );
            }
            return renderItem(section, false);
          })}
        </SortableContext>
      </div>
    </DndContext>
  );
};

export default SortableMultiColumnList;
